/**
 * CSV Import Page
 *
 * Displays saved import templates as cards with a "Neuer Import" button
 * that opens the import wizard in a dialog.
 */

import { useRef, useEffect, useState, useMemo } from 'react'
import {
  Upload,
  FileText,
  Settings,
  Play,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  X,
  FileSpreadsheet,
  Calendar,
  Save,
  Search,
  Loader2,
  Trash2,
  Database,
  Eye,
  RefreshCw,
} from 'lucide-react'
import { useCSVImportStore } from '../stores/csvImportStore'
import {
  useUploadCSV,
  useExecuteCSVImport,
  useCreateCSVTemplate,
  useCSVTemplates,
  useCSVTemplate,
  useDeleteCSVTemplate,
  useCSVImportedData,
  useCSVImportedValues,
} from '../hooks/csvImportQueries'
import { useEditions } from '../hooks/queries'
import { ColumnMapper } from '../components/ColumnMapper'
import { TemplateSelector } from '../components/TemplateSelector'
import type {
  CSVImportStep,
  EntityLevel,
  TargetTable,
  ImportTemplateInfo,
  ImportedDatasetInfo,
  ColumnMappingConfig,
} from '../types'
import { cn } from '../../lib/utils'

/** Preset for the import dialog: pre-apply a template (Folgejahres-Update). */
interface ImportPreset {
  templateId: number
  year?: number
}

const STEPS: { id: CSVImportStep; label: string; icon: React.ReactNode }[] = [
  { id: 'upload', label: 'Datei-Upload', icon: <Upload className="w-5 h-5" /> },
  { id: 'preview', label: 'Vorschau', icon: <FileText className="w-5 h-5" /> },
  { id: 'mapping', label: 'Spalten-Mapping', icon: <Settings className="w-5 h-5" /> },
  { id: 'progress', label: 'Import', icon: <Play className="w-5 h-5" /> },
  { id: 'complete', label: 'Abschluss', icon: <CheckCircle2 className="w-5 h-5" /> },
]

const ENTITY_LEVELS: { value: EntityLevel; label: string }[] = [
  { value: 'federal', label: 'Bund' },
  { value: 'state', label: 'Bundesland' },
  { value: 'government_district', label: 'Regierungsbezirk' },
  { value: 'county', label: 'Kreis' },
  { value: 'municipal_association', label: 'Gemeindeverband' },
  { value: 'municipality', label: 'Gemeinde' },
]

const TARGET_TABLES: { value: TargetTable; label: string; description: string }[] = [
  {
    value: 'indicator_values',
    label: 'Indikatoren',
    description: 'Flexibles Format für beliebige Daten',
  },
]

// ============================================================================
// Step Indicator (compact version for dialogs)
// ============================================================================

function StepIndicator({ currentStep }: { currentStep: CSVImportStep }) {
  const currentIndex = STEPS.findIndex((s) => s.id === currentStep)
  const currentStepData = STEPS[currentIndex]

  return (
    <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
      <div className="flex items-center gap-1.5">
        {STEPS.map((step, index) => {
          const isCompleted = index < currentIndex
          const isCurrent = index === currentIndex

          return (
            <div
              key={step.id}
              className={cn(
                'w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-colors',
                isCompleted && 'bg-green-600 text-white',
                isCurrent && 'bg-blue-600 text-white',
                !isCompleted && !isCurrent && 'bg-gray-200 text-gray-500'
              )}
            >
              {isCompleted ? <CheckCircle2 className="w-3.5 h-3.5" /> : index + 1}
            </div>
          )
        })}
      </div>
      <span className="text-sm font-medium text-gray-700">
        {currentStepData?.label}
      </span>
    </div>
  )
}

// ============================================================================
// Upload Step
// ============================================================================

function UploadStep() {
  const {
    file,
    setFile,
    setStep,
    setUploadResponse,
    setUploading,
    setError,
  } = useCSVImportStore()
  const uploadMutation = useUploadCSV()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null
    setFile(selectedFile)
    setError(null)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile) {
      setFile(droppedFile)
      setError(null)
    }
  }

  const handleUpload = async () => {
    if (!file) {
      setError('Bitte wählen Sie eine CSV-Datei aus')
      return
    }

    setUploading(true)
    setError(null)

    try {
      const response = await uploadMutation.mutateAsync({
        file,
      })
      setUploadResponse(response)
      setStep('preview')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload fehlgeschlagen')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900">CSV-Datei hochladen</h2>
        <p className="text-gray-500 text-sm mt-1">
          Laden Sie eine CSV-Datei hoch. Die Entitätsebene wird automatisch erkannt.
        </p>
      </div>

      {/* File Upload */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          CSV-Datei *
        </label>
        <div
          className={cn(
            'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
            file ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-gray-400',
            isDragging && 'border-blue-500 bg-blue-50'
          )}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt,.tsv"
            className="hidden"
            onChange={handleFileChange}
          />
          {file ? (
            <div className="flex items-center justify-center gap-2 text-green-700">
              <FileSpreadsheet className="w-5 h-5" />
              <span className="text-sm">{file.name}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setFile(null)
                }}
                className="ml-2 p-1 hover:bg-green-100 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="text-gray-500">
              <FileSpreadsheet className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm">Klicken oder Datei hierher ziehen</p>
              <p className="text-xs mt-1">.csv, .txt, .tsv</p>
            </div>
          )}
        </div>
      </div>

      {/* Upload Button */}
      <div className="flex justify-end pt-2">
        <button
          onClick={handleUpload}
          disabled={!file || uploadMutation.isPending}
          className={cn(
            'inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors text-sm',
            file && !uploadMutation.isPending
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          )}
        >
          {uploadMutation.isPending ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Wird analysiert...
            </>
          ) : (
            <>
              Weiter
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// Preview Step
// ============================================================================

function PreviewStep() {
  const { uploadResponse, entityLevel, setStep } = useCSVImportStore()

  if (!uploadResponse) return null

  const levelLabel = ENTITY_LEVELS.find((l) => l.value === entityLevel)?.label || entityLevel

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Vorschau</h2>
        <p className="text-gray-500 text-sm mt-1">
          Überprüfen Sie die erkannten Einstellungen und Spalten.
        </p>
      </div>

      {/* Detected Settings */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <span className="text-xs text-blue-600 uppercase font-medium">Entitätsebene</span>
            <p className="text-blue-800 font-medium">{levelLabel}</p>
          </div>
          <div>
            <span className="text-xs text-blue-600 uppercase font-medium">Trennzeichen</span>
            <p className="text-blue-800 font-medium font-mono">
              {uploadResponse.detected_delimiter === '\t' ? 'Tab' : uploadResponse.detected_delimiter}
            </p>
          </div>
          <div>
            <span className="text-xs text-blue-600 uppercase font-medium">Kodierung</span>
            <p className="text-blue-800 font-medium">{uploadResponse.detected_encoding}</p>
          </div>
          <div>
            <span className="text-xs text-blue-600 uppercase font-medium">Zeilen</span>
            <p className="text-blue-800 font-medium">{uploadResponse.row_count.toLocaleString('de-DE')}</p>
          </div>
        </div>
      </div>

      {/* Suggested Template */}
      {uploadResponse.suggested_template && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-green-800 text-sm">
            <CheckCircle2 className="w-4 h-4" />
            <span className="font-medium">
              Template erkannt: {uploadResponse.suggested_template.name}
            </span>
          </div>
          <p className="text-green-700 text-xs mt-1">
            Anbieter: {uploadResponse.suggested_template.provider}
          </p>
        </div>
      )}

      {/* Columns Preview */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
          <h3 className="font-medium text-sm text-gray-900">
            Spalten ({uploadResponse.columns.length})
          </h3>
        </div>
        <div className="max-h-60 overflow-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Spaltenname
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Typ
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Beispielwerte
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {uploadResponse.columns.map((col) => (
                <tr key={col.name}>
                  <td className="px-3 py-2 font-medium text-gray-900">{col.name}</td>
                  <td className="px-3 py-2 text-gray-500">{col.detected_type}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {col.sample_values.slice(0, 3).map((val, i) => (
                        <span
                          key={i}
                          className="inline-block px-1.5 py-0.5 text-xs bg-gray-100 rounded truncate max-w-[100px]"
                          title={val}
                        >
                          {val}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-2">
        <button
          onClick={() => setStep('upload')}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück
        </button>
        <button
          onClick={() => setStep('mapping')}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Weiter
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// Mapping Step
// ============================================================================

function MappingStep({ onComplete }: { onComplete: () => void }) {
  const {
    uploadResponse,
    entityLevel,
    columnMappings,
    targetTable,
    keyColumn,
    keyType,
    selectedTemplate,
    referenceYear,
    editionMode,
    editionName,
    targetEditionId,
    saveAsTemplate,
    templateProvider,
    updateColumnMapping,
    setKeyColumn,
    setKeyType,
    applyTemplate,
    setSelectedTemplate,
    setReferenceYear,
    setEditionMode,
    setTargetEditionId,
    setSaveAsTemplate,
    setTemplateProvider,
    setStep,
    reset,
  } = useCSVImportStore()

  const executeMutation = useExecuteCSVImport()
  const createTemplateMutation = useCreateCSVTemplate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [jobStarted, setJobStarted] = useState(false)
  const [createdJobId, setCreatedJobId] = useState<string | null>(null)

  const { data: editionsData } = useEditions('GV100')
  const currentYear = new Date().getFullYear()

  // Default to the latest GV100 edition (CSV imports always attach to one)
  useEffect(() => {
    if (!targetEditionId && editionsData?.editions?.length) {
      const latest =
        editionsData.editions.find((e) => e.is_latest) || editionsData.editions[0]
      setEditionMode('update_existing')
      setTargetEditionId(latest.id)
    }
  }, [editionsData, targetEditionId, setEditionMode, setTargetEditionId])

  const handleNewImport = () => {
    reset()
    onComplete()
  }

  // Show job started confirmation
  if (jobStarted && createdJobId) {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-3">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-base font-semibold text-gray-900">Job gestartet!</h2>
          <p className="text-gray-500 text-sm mt-1">
            Der CSV-Import wurde erfolgreich in die Warteschlange gestellt.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            <strong>Job-ID:</strong> {createdJobId}
          </p>
          <p className="text-xs text-blue-600 mt-1">
            Sie können den Fortschritt im Dashboard oder unter Jobs verfolgen.
          </p>
        </div>

        <div className="flex justify-center pt-2">
          <button
            onClick={handleNewImport}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Schließen
          </button>
        </div>
      </div>
    )
  }

  if (!uploadResponse) return null

  const handleStartImport = async () => {
    if (isSubmitting || !uploadResponse?.upload_id) return
    setIsSubmitting(true)

    try {
      // The template used for this import (usage counter + data source naming):
      // either the applied template or the one saved right now.
      let usedTemplateId: number | undefined = selectedTemplate?.id

      // Save template first if requested
      if (saveAsTemplate) {
        try {
          const templateMappings: Record<string, ColumnMappingConfig> = {}
          columnMappings.forEach((m) => {
            templateMappings[m.csv_column] = {
              csv_column: m.csv_column,
              target: m.target,
              data_type: m.data_type,
              transform: m.transform ?? null,
              skip: m.skip,
            }
          })
          const created = await createTemplateMutation.mutateAsync({
            name: saveAsTemplate,
            provider: templateProvider || 'custom',
            entity_level: entityLevel,
            target_table: targetTable,
            delimiter: uploadResponse.detected_delimiter,
            encoding: uploadResponse.detected_encoding,
            key_column: keyColumn,
            key_type: keyType,
            column_mappings: templateMappings,
          })
          usedTemplateId = created.id
        } catch (err) {
          console.error('Failed to save template:', err)
        }
      }

      // Start the import
      const result = await executeMutation.mutateAsync({
        uploadId: uploadResponse.upload_id,
        params: {
          column_mappings: columnMappings,
          target_table: targetTable,
          entity_level: entityLevel,
          key_column: keyColumn,
          key_type: keyType,
          reference_year: referenceYear,
          edition_mode: editionMode,
          edition_name: editionMode === 'create_new' ? editionName || undefined : undefined,
          target_edition_id: editionMode === 'update_existing' ? targetEditionId || undefined : undefined,
          template_id: usedTemplateId,
        },
        background: true,
        force: false,
      })

      if (result.job_id) {
        setCreatedJobId(result.job_id)
        setJobStarted(true)
        setIsSubmitting(false)
      }
    } catch (err) {
      console.error('Import failed:', err)
      setIsSubmitting(false)
    }
  }

  const handleTemplateSelect = (
    template: ImportTemplateInfo,
    mappings: Record<string, ColumnMappingConfig>
  ) => {
    applyTemplate(template, mappings)
  }

  const handleTemplateClear = () => {
    setSelectedTemplate(null)
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Spalten-Mapping</h2>
        <p className="text-gray-500 text-sm mt-1">
          Ordnen Sie die CSV-Spalten den Zielfeldern zu.
        </p>
      </div>

      {/* Template Selector */}
      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
        <h3 className="font-medium text-sm text-gray-900 mb-2 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Gespeicherte Templates
        </h3>
        <TemplateSelector
          entityLevel={entityLevel}
          selectedTemplate={selectedTemplate}
          currentMappings={columnMappings}
          onSelect={handleTemplateSelect}
          onClear={handleTemplateClear}
        />
      </div>

      {/* Column Mapper */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
          <h3 className="font-medium text-sm text-gray-900">Spalten-Zuordnung</h3>
        </div>
        <ColumnMapper
          columns={uploadResponse.columns}
          mappings={columnMappings}
          targetTable={targetTable}
          keyColumn={keyColumn}
          onMappingChange={updateColumnMapping}
          onKeyColumnChange={setKeyColumn}
        />
      </div>

      {/* Key Type Selection */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Schlüsseltyp
        </label>
        <select
          value={keyType}
          onChange={(e) => setKeyType(e.target.value as 'ags_5' | 'ags_8' | 'ars_12')}
          className="block w-full max-w-xs rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
        >
          <option value="ags_5">AGS 5-stellig (Kreise)</option>
          <option value="ags_8">AGS 8-stellig (Gemeinden)</option>
          <option value="ars_12">ARS 12-stellig</option>
        </select>
      </div>

      {/* Reference Year & Edition */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Referenzjahr
          </label>
          <input
            type="number"
            value={referenceYear}
            onChange={(e) => setReferenceYear(parseInt(e.target.value) || currentYear)}
            min={2000}
            max={currentYear + 1}
            className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          />
        </div>

        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            Edition (GV100-Master)
          </label>
          <select
            value={targetEditionId || ''}
            onChange={(e) => {
              setEditionMode('update_existing')
              setTargetEditionId(e.target.value ? Number(e.target.value) : null)
            }}
            className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          >
            <option value="">Edition wählen...</option>
            {editionsData?.editions?.map((edition) => (
              <option key={edition.id} value={edition.id}>
                {edition.edition} (Referenzjahr {edition.reference_year})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Save as Template */}
      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!saveAsTemplate}
            onChange={(e) => setSaveAsTemplate(e.target.checked ? 'Neues Template' : null)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <Save className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Als Template speichern</span>
        </label>
        {saveAsTemplate && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <input
              type="text"
              value={saveAsTemplate}
              onChange={(e) => setSaveAsTemplate(e.target.value)}
              placeholder="Template-Name"
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            />
            <input
              type="text"
              value={templateProvider}
              onChange={(e) => setTemplateProvider(e.target.value)}
              placeholder="Anbieter (z.B. csv_import)"
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            />
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-2">
        <button
          onClick={() => setStep('preview')}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück
        </button>
        <button
          onClick={handleStartImport}
          disabled={!keyColumn || isSubmitting}
          className={cn(
            'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm',
            keyColumn && !isSubmitting
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          )}
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Wird gestartet...
            </>
          ) : (
            <>
              Import starten
              <Play className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// Progress Step (unused in dialog mode, kept for compatibility)
// ============================================================================

function ProgressStep() {
  return (
    <div className="flex flex-col items-center justify-center py-6">
      <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600" />
      <p className="mt-4 text-sm text-gray-600">Import-Job wird verarbeitet...</p>
    </div>
  )
}

// ============================================================================
// Complete Step (unused in dialog mode, kept for compatibility)
// ============================================================================

function CompleteStep({ onComplete }: { onComplete: () => void }) {
  const { importResult, reset } = useCSVImportStore()

  const handleFinish = () => {
    reset()
    onComplete()
  }

  if (!importResult) return null

  const isSuccess = importResult.status === 'completed' && importResult.failed_records === 0

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900">
          {isSuccess ? 'Import abgeschlossen' : 'Import abgeschlossen mit Fehlern'}
        </h2>
      </div>

      <div
        className={cn(
          'p-3 rounded-lg border text-sm',
          isSuccess
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-yellow-50 border-yellow-200 text-yellow-800'
        )}
      >
        <div className="flex items-center gap-2">
          {isSuccess ? (
            <CheckCircle2 className="w-4 h-4" />
          ) : (
            <AlertCircle className="w-4 h-4" />
          )}
          <span className="font-medium">
            {importResult.successful_records.toLocaleString('de-DE')} Datensätze erfolgreich importiert
          </span>
        </div>
      </div>

      <div className="flex justify-center pt-2">
        <button
          onClick={handleFinish}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Schließen
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// Import Dialog
// ============================================================================

function ImportDialog({
  isOpen,
  onClose,
  onImportComplete,
  preset,
}: {
  isOpen: boolean
  onClose: () => void
  onImportComplete: () => void
  preset?: ImportPreset | null
}) {
  const { step, error, reset, uploadResponse, applyTemplate, setReferenceYear } =
    useCSVImportStore()

  // Folgejahres-Update: pre-apply the dataset's template once the file is uploaded
  const { data: presetTemplate } = useCSVTemplate(preset?.templateId ?? null)
  const presetApplied = useRef(false)

  useEffect(() => {
    if (!isOpen) {
      presetApplied.current = false
      return
    }
    if (preset && presetTemplate && uploadResponse && !presetApplied.current) {
      applyTemplate(presetTemplate, presetTemplate.column_mappings as Record<string, ColumnMappingConfig>)
      if (preset.year) {
        setReferenceYear(preset.year)
      }
      presetApplied.current = true
    }
  }, [isOpen, preset, presetTemplate, uploadResponse, applyTemplate, setReferenceYear])

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleComplete = () => {
    onImportComplete()
    handleClose()
  }

  if (!isOpen) return null

  // Use wider dialog for mapping step
  const dialogWidth = step === 'mapping' ? 'max-w-4xl' : 'max-w-2xl'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className={cn('bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-hidden flex flex-col', dialogWidth)}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold">CSV Import</h2>
            <p className="text-sm text-gray-500">Daten aus CSV-Dateien mit flexiblem Spalten-Mapping importieren</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <StepIndicator currentStep={step} />

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-800 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Step Content */}
          {step === 'upload' && <UploadStep />}
          {step === 'preview' && <PreviewStep />}
          {step === 'mapping' && <MappingStep onComplete={handleComplete} />}
          {step === 'progress' && <ProgressStep />}
          {step === 'complete' && <CompleteStep onComplete={handleComplete} />}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Template Card
// ============================================================================

function TemplateCard({
  template,
  onDelete,
  onImport,
}: {
  template: ImportTemplateInfo
  onDelete?: () => void
  onImport?: () => void
}) {
  const levelLabel = ENTITY_LEVELS.find((l) => l.value === template.entity_level)?.label || template.entity_level
  const tableLabel = TARGET_TABLES.find((t) => t.value === template.target_table)?.label || template.target_table

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
              {template.provider}
            </span>
            {template.is_system && (
              <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                System
              </span>
            )}
          </div>
          <h3 className="font-semibold text-gray-900 text-sm">
            {template.name}
          </h3>
        </div>
        {onDelete && !template.is_system && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
            className="p-1 text-gray-400 hover:text-red-600 rounded"
            title="Template löschen"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Description */}
      {template.description && (
        <p className="text-xs text-gray-500 mb-2 line-clamp-2">
          {template.description}
        </p>
      )}

      {/* Tags */}
      <div className="flex flex-wrap gap-1 mb-3">
        <span className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded border border-green-200">
          {levelLabel}
        </span>
        <span className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200">
          {tableLabel}
        </span>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-100">
        <span>
          {template.use_count} {template.use_count === 1 ? 'Verwendung' : 'Verwendungen'}
        </span>
        <span>
          {template.last_used_at
            ? new Date(template.last_used_at).toLocaleDateString('de-DE')
            : 'Nie verwendet'}
        </span>
      </div>

      {/* Import with this template */}
      {onImport && (
        <button
          onClick={onImport}
          className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50"
        >
          <Upload className="w-3.5 h-3.5" />
          Import mit Template
        </button>
      )}
    </div>
  )
}

// ============================================================================
// Templates Grid
// ============================================================================

function TemplatesGrid({
  onRefresh,
  onImportWithTemplate,
}: {
  onRefresh: number
  onImportWithTemplate?: (template: ImportTemplateInfo) => void
}) {
  const { data: templates, isLoading, refetch } = useCSVTemplates()
  const deleteMutation = useDeleteCSVTemplate()
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    if (onRefresh > 0) {
      refetch()
    }
  }, [onRefresh, refetch])

  // Filter and sort templates
  const filteredTemplates = useMemo(() => {
    if (!templates) return []

    let result = [...templates]

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase()
      result = result.filter((t) =>
        t.name.toLowerCase().includes(term) ||
        t.provider.toLowerCase().includes(term) ||
        (t.description && t.description.toLowerCase().includes(term))
      )
    }

    // Sort: system templates first, then by name
    result.sort((a, b) => {
      if (a.is_system !== b.is_system) return a.is_system ? -1 : 1
      return a.name.localeCompare(b.name)
    })

    return result
  }, [templates, searchTerm])

  const handleDelete = async (id: number) => {
    if (confirm('Template wirklich löschen?')) {
      await deleteMutation.mutateAsync(id)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    )
  }

  if (!templates || templates.length === 0) {
    return (
      <div className="text-center py-12">
        <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-500 mb-2">Keine Import-Templates vorhanden</p>
        <p className="text-sm text-gray-400">
          Klicken Sie auf "Neuer Import" und speichern Sie ein Template
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Template suchen..."
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        {searchTerm && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
            {filteredTemplates.length} von {templates.length}
          </span>
        )}
      </div>

      {/* Grid */}
      {filteredTemplates.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Keine Ergebnisse für "{searchTerm}"
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onDelete={() => handleDelete(template.id)}
              onImport={onImportWithTemplate ? () => onImportWithTemplate(template) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Imported Data: Values Dialog
// ============================================================================

function ValuesDialog({
  dataset,
  onClose,
}: {
  dataset: ImportedDatasetInfo
  onClose: () => void
}) {
  const [indicatorId, setIndicatorId] = useState<number | null>(
    dataset.indicators[0]?.id ?? null
  )
  const selectedIndicator = dataset.indicators.find((i) => i.id === indicatorId)
  const availableYears = selectedIndicator?.years ?? dataset.years
  const [year, setYear] = useState<number | undefined>(
    availableYears.length > 0 ? availableYears[availableYears.length - 1] : undefined
  )

  // Keep year valid when switching indicators
  useEffect(() => {
    if (year !== undefined && !availableYears.includes(year)) {
      setYear(availableYears.length > 0 ? availableYears[availableYears.length - 1] : undefined)
    }
  }, [indicatorId]) // eslint-disable-line react-hooks/exhaustive-deps

  const { data, isLoading } = useCSVImportedValues(dataset.id, indicatorId, year)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold">{dataset.dataset_name}</h2>
            <p className="text-sm text-gray-500">
              Importierte Werte ansehen
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Controls */}
        <div className="px-6 py-3 border-b flex flex-wrap items-center gap-3">
          <select
            value={indicatorId ?? ''}
            onChange={(e) => setIndicatorId(e.target.value ? Number(e.target.value) : null)}
            className="rounded-lg border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
          >
            {dataset.indicators.map((ind) => (
              <option key={ind.id} value={ind.id}>
                {ind.name} ({ind.code})
              </option>
            ))}
          </select>
          <select
            value={year ?? ''}
            onChange={(e) => setYear(e.target.value ? Number(e.target.value) : undefined)}
            className="rounded-lg border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">Alle Jahre</option>
            {availableYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          {data && (
            <span className="text-sm text-gray-500 ml-auto">
              {data.rows.length.toLocaleString('de-DE')} von{' '}
              {data.total.toLocaleString('de-DE')} Werten
            </span>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : !data || data.rows.length === 0 ? (
            <div className="text-center py-12 text-gray-500">Keine Werte gefunden.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Schlüssel
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Gebiet
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Jahr
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Wert
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.rows.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-1.5 font-mono text-xs text-gray-500">
                      {row.entity_key}
                    </td>
                    <td className="px-4 py-1.5 text-gray-900">
                      {row.entity_name ?? `#${row.entity_id}`}
                    </td>
                    <td className="px-4 py-1.5 text-gray-500">{row.reference_year}</td>
                    <td className="px-4 py-1.5 text-right font-mono text-gray-900">
                      {row.value !== null
                        ? isNaN(Number(row.value))
                          ? row.value
                          : Number(row.value).toLocaleString('de-DE', { maximumFractionDigits: 4 })
                        : '–'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Imported Data Section
// ============================================================================

const ENTITY_TYPE_LABELS: Record<string, string> = {
  federal: 'Bund',
  state: 'Land',
  government_district: 'Reg.-Bezirk',
  county: 'Kreis',
  municipal_association: 'Gemeindeverband',
  municipality: 'Gemeinde',
}

function ImportedDataCard({
  dataset,
  onShowValues,
  onUpdateImport,
}: {
  dataset: ImportedDatasetInfo
  onShowValues: () => void
  onUpdateImport: (() => void) | null
}) {
  const latestYear = dataset.years.length > 0 ? dataset.years[dataset.years.length - 1] : null

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow flex flex-col">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
              {dataset.provider}
            </span>
            {dataset.parent_edition && (
              <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                GV100 {dataset.parent_edition}
              </span>
            )}
          </div>
          <h3 className="font-semibold text-gray-900 text-sm">{dataset.dataset_name}</h3>
        </div>
        <Database className="w-4 h-4 text-gray-300 shrink-0" />
      </div>

      {/* Tags: years + entity types */}
      <div className="flex flex-wrap gap-1 mb-2">
        {dataset.years.map((y) => (
          <span
            key={y}
            className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded border border-green-200"
          >
            {y}
          </span>
        ))}
        {dataset.entity_types.map((et) => (
          <span
            key={et}
            className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200"
          >
            {ENTITY_TYPE_LABELS[et] ?? et}
          </span>
        ))}
      </div>

      {/* Stats */}
      <div className="text-xs text-gray-500 space-y-0.5 mb-3">
        <p>
          {dataset.indicators.length}{' '}
          {dataset.indicators.length === 1 ? 'Indikator' : 'Indikatoren'} ·{' '}
          {dataset.value_count.toLocaleString('de-DE')} Werte
        </p>
        {dataset.import_date && (
          <p>
            Import: {new Date(dataset.import_date).toLocaleDateString('de-DE')}
            {dataset.import_file && (
              <span className="text-gray-400"> ({dataset.import_file})</span>
            )}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2 border-t border-gray-100 mt-auto">
        <button
          onClick={onShowValues}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <Eye className="w-3.5 h-3.5" />
          Daten ansehen
        </button>
        {onUpdateImport && (
          <button
            onClick={onUpdateImport}
            title={
              latestYear
                ? `Neue CSV mit Template importieren (Vorschlag: Referenzjahr ${latestYear + 1})`
                : 'Neue CSV mit Template importieren'
            }
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Folgejahr importieren
          </button>
        )}
      </div>
    </div>
  )
}

function ImportedDataSection({
  onUpdateImport,
}: {
  onUpdateImport: (preset: ImportPreset) => void
}) {
  const { data: datasets, isLoading } = useCSVImportedData()
  const [valuesDataset, setValuesDataset] = useState<ImportedDatasetInfo | null>(null)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    )
  }

  if (!datasets || datasets.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-200">
        <Database className="w-10 h-10 mx-auto mb-3 text-gray-300" />
        <p className="text-gray-500 text-sm">Noch keine Daten importiert</p>
        <p className="text-xs text-gray-400 mt-1">
          Nach einem Import erscheinen die Datensätze hier zur Kontrolle.
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {datasets.map((dataset) => (
          <ImportedDataCard
            key={dataset.id}
            dataset={dataset}
            onShowValues={() => setValuesDataset(dataset)}
            onUpdateImport={
              dataset.template_id
                ? () =>
                    onUpdateImport({
                      templateId: dataset.template_id!,
                      year:
                        dataset.years.length > 0
                          ? dataset.years[dataset.years.length - 1] + 1
                          : undefined,
                    })
                : null
            }
          />
        ))}
      </div>

      {valuesDataset && (
        <ValuesDialog dataset={valuesDataset} onClose={() => setValuesDataset(null)} />
      )}
    </>
  )
}

// ============================================================================
// Main Component
// ============================================================================

export function CSVImportWizard() {
  const [showImportDialog, setShowImportDialog] = useState(false)
  const [importPreset, setImportPreset] = useState<ImportPreset | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  const handleImportComplete = () => {
    setRefreshKey((k) => k + 1)
  }

  const openImport = (preset: ImportPreset | null = null) => {
    setImportPreset(preset)
    setShowImportDialog(true)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CSV Import</h1>
          <p className="text-gray-500 mt-1">
            Daten aus CSV-Dateien mit flexiblem Spalten-Mapping importieren
          </p>
        </div>
        <button
          onClick={() => openImport()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Upload className="w-4 h-4" />
          Neuer Import
        </button>
      </div>

      {/* Imported Data */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Importierte Daten</h2>
        </div>
        <ImportedDataSection onUpdateImport={(preset) => openImport(preset)} />
      </section>

      {/* Templates */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Templates</h2>
        </div>
        <TemplatesGrid onRefresh={refreshKey} onImportWithTemplate={(t) => openImport({ templateId: t.id })} />
      </section>

      {/* Import Dialog */}
      <ImportDialog
        isOpen={showImportDialog}
        onClose={() => {
          setShowImportDialog(false)
          setImportPreset(null)
        }}
        onImportComplete={handleImportComplete}
        preset={importPreset}
      />
    </div>
  )
}
